import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  Button,
  Icon,
  Spinner,
  Alert,
  SimpleGrid,
  Progress,
  Link,
  Code,
} from '@chakra-ui/react'
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi'
import { apiConfig } from '../../config/api'
import { VisibilityScore } from '../../components/onboarding/VisibilityScore'

interface PractitionerDetail {
  practitioner_id: string
  email?: string
  name?: string
  step: number
  visibility_score?: number
  basic_info?: Record<string, any>
  detailed_interview?: Record<string, any>
  campaigns: Array<{
    id: string
    status: string
    impressions_total: number
    impressions_delivered: number
    created_at?: string
  }>
  created_at?: string
  updated_at?: string
}

export const PractitionerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [practitioner, setPractitioner] = useState<PractitionerDetail | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPractitioner = async () => {
    if (!id) return

    const token = localStorage.getItem('firebase_id_token')
    if (!token) {
      setError('Authentication required')
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(
        `${apiConfig.baseURL}/api/v1/admin/practitioners/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to load practitioner')
      }

      const result = await response.json()
      setPractitioner(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load practitioner')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPractitioner()
  }, [id])

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minH="400px">
        <VStack gap={4}>
          <Spinner size="xl" color="brand.500" />
          <Text color="gray.600">Loading practitioner...</Text>
        </VStack>
      </Box>
    )
  }

  if (error || !practitioner) {
    return (
      <Container maxW="7xl">
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Content>
            {error || 'Failed to load practitioner'}
          </Alert.Content>
        </Alert.Root>
      </Container>
    )
  }

  return (
    <Container maxW="7xl">
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack>
          <Button
            leftIcon={<Icon as={FiArrowLeft} />}
            onClick={() => navigate('/admin/practitioners')}
            variant="ghost"
          >
            Back to Practitioners
          </Button>
        </HStack>

        <HStack justify="space-between" align="end">
          <VStack align="start" gap={2}>
            <Heading size="2xl" color="gray.900">
              Practitioner Details
            </Heading>
            <Text fontSize="sm" color="gray.500" fontFamily="mono">
              {practitioner.practitioner_id}
            </Text>
          </VStack>
          <Button
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={fetchPractitioner}
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>

        {/* Key Info */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          <Card.Root>
            <Card.Body p={5}>
              <VStack align="start" gap={2}>
                <Text fontSize="sm" color="gray.500">
                  Name
                </Text>
                <Text fontWeight="bold" fontSize="lg">
                  {practitioner.name || 'Unknown'}
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body p={5}>
              <VStack align="start" gap={2}>
                <Text fontSize="sm" color="gray.500">
                  Email
                </Text>
                <Text>{practitioner.email || '-'}</Text>
              </VStack>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body p={5}>
              <VStack align="start" gap={2}>
                <Text fontSize="sm" color="gray.500">
                  Onboarding Step
                </Text>
                <VStack align="start" gap={1} w="full">
                  <Text fontWeight="bold" fontSize="lg">
                    Step {practitioner.step} / 5
                  </Text>
                  <Progress
                    value={(practitioner.step / 5) * 100}
                    size="sm"
                    colorPalette="brand"
                    w="full"
                  />
                </VStack>
              </VStack>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Visibility Score */}
        {practitioner.visibility_score !== undefined && (
          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" color="gray.900">
                  Visibility Score
                </Heading>
                <VisibilityScore
                  score={practitioner.visibility_score}
                  breakdown={
                    practitioner.basic_info
                      ? {
                          completeness: 0,
                          quality: 0,
                        }
                      : undefined
                  }
                  showBreakdown={false}
                  showRecommendations={false}
                />
              </VStack>
            </Card.Body>
          </Card.Root>
        )}

        {/* Basic Info */}
        {practitioner.basic_info && (
          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" color="gray.900">
                  Basic Information
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  {Object.entries(practitioner.basic_info).map(([key, value]) => (
                    <VStack key={key} align="start" gap={1}>
                      <Text fontSize="sm" color="gray.500">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, (l) =>
                          l.toUpperCase()
                        )}
                      </Text>
                      <Text>
                        {typeof value === 'object'
                          ? JSON.stringify(value, null, 2)
                          : String(value)}
                      </Text>
                    </VStack>
                  ))}
                </SimpleGrid>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}

        {/* Detailed Interview */}
        {practitioner.detailed_interview && (
          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" color="gray.900">
                  Detailed Interview
                </Heading>
                <Box>
                  <Code p={4} display="block" whiteSpace="pre-wrap">
                    {JSON.stringify(practitioner.detailed_interview, null, 2)}
                  </Code>
                </Box>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}

        {/* Campaigns */}
        {practitioner.campaigns.length > 0 && (
          <Card.Root>
            <Card.Body p={6}>
              <VStack align="stretch" gap={4}>
                <Heading size="md" color="gray.900">
                  Associated Campaigns
                </Heading>
                <VStack align="stretch" gap={2}>
                  {practitioner.campaigns.map((campaign) => (
                    <Card.Root key={campaign.id} variant="outline">
                      <Card.Body p={4}>
                        <HStack justify="space-between">
                          <VStack align="start" gap={1}>
                            <Link
                              color="blue.500"
                              onClick={() =>
                                navigate(`/admin/campaigns/${campaign.id}`)
                              }
                              cursor="pointer"
                              fontWeight="medium"
                            >
                              {campaign.id.substring(0, 12)}...
                            </Link>
                            <Text fontSize="sm" color="gray.500">
                              {campaign.impressions_delivered.toLocaleString()} /{' '}
                              {campaign.impressions_total.toLocaleString()} impressions
                            </Text>
                          </VStack>
                          <Text fontSize="sm" color="gray.500">
                            {campaign.status}
                          </Text>
                        </HStack>
                      </Card.Body>
                    </Card.Root>
                  ))}
                </VStack>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}
      </VStack>
    </Container>
  )
}

